import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../../hooks/useApi';
import { initials } from '../../utils/format';
import {
  buildTree, sortTree, visibleLeaves, fitTransform, zoomAt, reportingCycles, ancestorIds, groupByTeam,
} from '../../utils/orgTree';
import { renderOrgChartPng } from '../../utils/orgChartExport';
import Modal from '../shared/Modal';

// The horizontal room one card occupies. The slot is wider than the card so
// siblings do not touch; the card is centred in it.
const NODE_W  = 170;
const CARD_W  = 150;
const STEM_H  = 20;
// Levels 0, 1 and 2 are open when the chart is first drawn; below that starts
// collapsed, or a large organisation opens as an unreadable wall.
const OPEN_TO_DEPTH = 2;

const isContractor = p => p?.employment_type?.toLowerCase().includes('contract');

// ─── Avatar ───────────────────────────────────────────────────────────────────
function OrgAvatar({ person, size = 64, selected, ctr, onClick }) {
  const color = person.avatar_color || '#A1723D';
  const border = ctr
    ? '2.5px dashed rgba(245,158,11,0.85)'
    : selected
      ? '3px solid #C9A84C'
      : '2px solid rgba(255,255,255,0.6)';

  return (
    <div
      onClick={onClick}
      title="Click to view photo"
      style={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}
    >
      {person.profile_photo ? (
        <img
          src={person.profile_photo}
          alt=""
          style={{
            width: size, height: size, borderRadius: '50%', objectFit: 'cover',
            border, boxShadow: '0 2px 10px rgba(0,0,0,0.18)', display: 'block',
          }}
        />
      ) : (
        <div style={{
          width: size, height: size, borderRadius: '50%', background: color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: Math.floor(size * 0.28), fontWeight: 700, color: '#fff',
          border, boxShadow: '0 2px 10px rgba(0,0,0,0.14)',
        }}>
          {initials(`${person.first_name} ${person.last_name}`)}
        </div>
      )}
      {selected && (
        <div style={{
          position: 'absolute', inset: -4, borderRadius: '50%',
          border: '2px solid #C9A84C', pointerEvents: 'none',
        }} />
      )}
    </div>
  );
}

// ─── Person card ──────────────────────────────────────────────────────────────
//
// Collapse state is NOT held here. It belongs to the page, because the width of
// every slot depends on what is collapsed, and a card cannot know that about its
// siblings. Keeping it local is what left three quarters of the canvas empty.
function PersonCard({
  node, onSelect, selectedId, onPhotoClick, dragState, isCollapsed, onToggle, isInCycle,
}) {
  const collapsed    = isCollapsed(node.id);
  const inCycle      = isInCycle(node.id);
  const hasChildren  = node.children.length > 0;
  const isSelected   = selectedId === node.id;
  const ctr          = isContractor(node);
  const slotW        = visibleLeaves(node, isCollapsed) * NODE_W;

  const guard = (fn) => (e) => {
    e.stopPropagation();
    if (dragState.current.moved) return;
    fn();
  };

  return (
    <div style={{ width: slotW, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div
        data-person-id={node.id}
        onClick={guard(() => onSelect(node))}
        style={{ width: CARD_W, cursor: 'pointer', userSelect: 'none' }}
        className={`flex flex-col items-center px-1.5 pt-2.5 pb-2 rounded-xl border transition-colors ${
          isSelected
            ? 'bg-gold/10 border-gold ring-1 ring-gold/40'
            : inCycle
              ? 'bg-amber-50 border-amber-300'
              : 'bg-white border-gray-200'
        }`}
      >
        <OrgAvatar
          person={node}
          size={60}
          selected={isSelected}
          ctr={ctr}
          onClick={guard(() => onPhotoClick(node))}
        />

        {ctr && (
          <div className="mt-1 px-1.5 rounded bg-amber-100 text-amber-700 text-[7px] font-bold tracking-wider">
            CTR
          </div>
        )}

        <div className={`${ctr ? 'mt-1' : 'mt-2'} text-center w-full px-1`}>
          <div className="text-[10px] font-bold text-navy leading-tight truncate">
            {node.first_name} {node.last_name}
          </div>
          {node.designation && (
            <div className="text-[8px] text-gray-500 leading-tight mt-0.5 truncate">{node.designation}</div>
          )}
          {node.team_name && (
            <div className="text-[8px] text-gray-400 leading-tight truncate">{node.team_name}</div>
          )}
        </div>

        {hasChildren && (
          <button
            onClick={guard(() => onToggle(node.id))}
            title={collapsed ? 'Show reports' : 'Hide reports'}
            className="mt-1.5 h-4 px-2 rounded-full bg-gray-100 text-gray-500 text-[8px] font-bold leading-none"
          >
            {collapsed ? `+${node.children.length}` : '−'}
          </button>
        )}
      </div>

      {/* Children */}
      {hasChildren && !collapsed && (
        <div className="flex flex-col items-center w-full mt-0.5">
          <div className="w-px bg-gray-200" style={{ height: STEM_H }} />
          <div style={{ display: 'flex', width: '100%' }}>
            {node.children.map((child, idx, arr) => {
              const cW       = visibleLeaves(child, isCollapsed) * NODE_W;
              const childCtr = isContractor(child);
              return (
                <div key={child.id} style={{ width: cW, flexShrink: 0 }} className="flex flex-col items-center">
                  <div style={{ position: 'relative', width: '100%', height: STEM_H }}>
                    {idx > 0 && (
                      <div className="absolute top-0 h-px bg-gray-200" style={{ left: 0, right: '50%' }} />
                    )}
                    {idx < arr.length - 1 && (
                      <div className="absolute top-0 h-px bg-gray-200" style={{ left: '50%', right: 0 }} />
                    )}
                    {childCtr ? (
                      <div style={{
                        position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                        top: 0, bottom: 0, width: 2,
                        backgroundImage: 'repeating-linear-gradient(to bottom, #FCD34D 0px, #FCD34D 4px, transparent 4px, transparent 8px)',
                      }} />
                    ) : (
                      <div
                        className="absolute w-px bg-gray-200"
                        style={{ left: '50%', transform: 'translateX(-50%)', top: 0, bottom: 0 }}
                      />
                    )}
                  </div>
                  <PersonCard
                    node={child}
                    onSelect={onSelect}
                    selectedId={selectedId}
                    onPhotoClick={onPhotoClick}
                    dragState={dragState}
                    isCollapsed={isCollapsed}
                    onToggle={onToggle}
                    isInCycle={isInCycle}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Team board ───────────────────────────────────────────────────────────────
function TeamMember({ person, isLead, selectedId, highlighted, onSelect, onPhotoClick }) {
  const ctr = isContractor(person);
  return (
    <div
      onClick={() => onSelect(person)}
      className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer ${
        selectedId === person.id
          ? 'bg-gold/10 border-gold'
          : highlighted
            ? 'bg-amber-50 border-amber-300'
            : 'bg-white border-gray-200 hover:bg-gray-50'
      }`}
    >
      <OrgAvatar
        person={person}
        size={28}
        ctr={ctr}
        onClick={e => { e.stopPropagation(); onPhotoClick(person); }}
      />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold text-navy truncate">{person.first_name} {person.last_name}</div>
        {person.designation && <div className="text-[8px] text-gray-500 truncate">{person.designation}</div>}
      </div>
      {isLead && <span className="text-[7px] font-bold bg-gold/15 text-gold px-1 py-0.5 rounded flex-shrink-0">LEAD</span>}
      {ctr && <span className="text-[7px] font-bold bg-amber-100 text-amber-700 px-1 py-0.5 rounded flex-shrink-0">CTR</span>}
    </div>
  );
}

function TeamBoard({ board, colorOf, selectedId, isHighlighted, onSelect, onPhotoClick }) {
  return (
    <div className="grid gap-3 items-start" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
      {board.map(team => (
        <div
          key={team.key ?? 'unassigned'}
          className="bg-gray-50 border border-gray-200 rounded"
          style={{ borderTop: `3px solid ${colorOf(team)}` }}
        >
          <div className="flex items-baseline justify-between px-2.5 pt-2 pb-1.5">
            <span className={`text-[10px] font-bold ${team.key ? 'text-navy' : 'text-gray-500'}`}>{team.name}</span>
            <span className="text-[8px] text-gray-400">{team.members.length + (team.lead ? 1 : 0)}</span>
          </div>
          <div className="flex flex-col gap-1 px-2 pb-2">
            {[team.lead, ...team.members].filter(Boolean).map(p => (
              <TeamMember
                key={p.id}
                person={p}
                isLead={p === team.lead}
                selectedId={selectedId}
                highlighted={isHighlighted(p)}
                onSelect={onSelect}
                onPhotoClick={onPhotoClick}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Detail pane ──────────────────────────────────────────────────────────────
function DetailPane({ person, manager, onClose, onPhotoClick }) {
  const ctr  = isContractor(person);
  const rows = [
    ['Employee ID',  person.employee_id],
    ['Type',         person.employment_type],
    ['Designation',  person.designation],
    ['Team',         person.team_name],
    ['Grade',        person.grade],
    ['Rate Band',    person.rate_band],
    ['Status',       person.employee_status],
    ['Clearance',    person.security_clearance && person.security_clearance !== 'none' ? person.security_clearance.toUpperCase() : null],
    ['Reporting To', manager ? `${manager.first_name} ${manager.last_name}` : null],
    ['Email',        person.email_work],
    ['Work Phone',   person.work_phone],
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl px-4 py-3.5 w-[230px] flex-shrink-0 overflow-y-auto">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <OrgAvatar person={person} size={48} ctr={ctr} onClick={() => onPhotoClick(person)} />
          <div>
            <div className="text-[11px] font-bold text-navy">
              {person.first_name} {person.last_name}
            </div>
            <div className="text-[8px] text-gray-400 mt-0.5">{person.designation || 'No title'}</div>
            {ctr && (
              <div className="mt-1 inline-block px-1.5 rounded bg-amber-100 text-amber-700 text-[7px] font-bold">
                CONTRACTOR
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-navy text-[12px] leading-none p-0.5 flex-shrink-0"
        >✕</button>
      </div>

      <div className="flex flex-col gap-1.5">
        {rows.map(([l, v]) => v ? (
          <div key={l} className="flex justify-between gap-2">
            <span className="text-[7px] font-bold text-gray-400 uppercase flex-shrink-0 pt-0.5">{l}</span>
            <span className="text-[9px] text-gray-700 text-right">{v}</span>
          </div>
        ) : null)}
      </div>
    </div>
  );
}

// ─── Photo lightbox ───────────────────────────────────────────────────────────
function PhotoLightbox({ person, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
      onClick={onClose}
    >
      <div style={{ position: 'relative', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
        {person.profile_photo ? (
          <img
            src={person.profile_photo}
            alt=""
            style={{ maxWidth: '80vw', maxHeight: '72vh', borderRadius: 20, objectFit: 'contain', border: '4px solid #C9A84C', boxShadow: '0 24px 64px rgba(0,0,0,0.6)', display: 'block' }}
          />
        ) : (
          <div style={{
            width: 240, height: 240, borderRadius: '50%', background: person.avatar_color || '#A1723D',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 80, fontWeight: 700, color: '#fff',
            border: '4px solid #C9A84C', boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          }}>
            {initials(`${person.first_name} ${person.last_name}`)}
          </div>
        )}
        <div style={{ marginTop: 14, color: '#fff' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{person.first_name} {person.last_name}</div>
          {person.designation && <div style={{ fontSize: 11, opacity: 0.55, marginTop: 3 }}>{person.designation}</div>}
        </div>
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: -14, right: -14, width: 30, height: 30, borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
            cursor: 'pointer', color: '#fff', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >×</button>
      </div>
    </div>
  );
}

// ─── Download for slides ──────────────────────────────────────────────────────
const findNode = (nodes, id) => {
  for (const n of nodes) {
    if (n.id === id) return n;
    const hit = findNode(n.children, id);
    if (hit) return hit;
  }
  return null;
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Choice({ name, value, current, onPick, children }) {
  return (
    <label className="flex items-center gap-1.5 text-[10px] text-gray-700 cursor-pointer">
      <input type="radio" name={name} checked={current === value} onChange={() => onPick(value)} />
      {children}
    </label>
  );
}

function ExportDialog({ roots, isCollapsed, teamColor, selected, onClose }) {
  const branchRoot = selected ? findNode(roots, selected.id) : null;
  const [from,       setFrom]       = useState('all');
  const [branches,   setBranches]   = useState('screen');
  const [withTitle,  setWithTitle]  = useState(true);
  const [withLegend, setWithLegend] = useState(true);
  const [background, setBackground] = useState('white');
  const [result,     setResult]     = useState(null);
  const [error,      setError]      = useState('');

  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const fromBranch = from === 'branch' && branchRoot;

  // Rendered on every change of option, so the preview is the file that the
  // button saves rather than an approximation of it.
  useEffect(() => {
    let cancelled = false;
    let url = null;
    setError('');
    renderOrgChartPng(
      fromBranch ? [branchRoot] : roots,
      branches === 'all' ? () => false : isCollapsed,
      node => teamColor[node.team_name] || null,
      {
        title: withTitle ? (fromBranch ? `${branchRoot.first_name} ${branchRoot.last_name} and team` : 'Organisation chart') : null,
        subtitle: `Alton Valley · ${today}`,
        legend: withLegend,
        background: background === 'white' ? '#FFFFFF' : null,
      },
    ).then(r => {
      if (cancelled) return;
      if (!r.blob) { setError('The browser could not draw a chart this size. Shut some branches, or start from one person.'); setResult(null); return; }
      url = URL.createObjectURL(r.blob);
      setResult({ ...r, url });
    }).catch(() => { if (!cancelled) { setError('The chart could not be drawn.'); setResult(null); } });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [roots, isCollapsed, teamColor, fromBranch, branchRoot, branches, withTitle, withLegend, background, today]);

  const download = () => {
    if (!result) return;
    const stamp = new Date().toISOString().slice(0, 10);
    const who = fromBranch ? `-${`${branchRoot.first_name}-${branchRoot.last_name}`.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : '';
    downloadBlob(result.blob, `alton-valley-org-chart${who}-${stamp}.png`);
    onClose();
  };

  // A 16:9 slide is 1.78 wide to 1 tall. Well past that, the chart is shrunk
  // to fit the slide's width and the names stop being readable from the back.
  const ratio = result ? result.width / result.height : 0;
  const tooWide = ratio > 3.5;

  return (
    <Modal
      title="Download org chart"
      onClose={onClose}
      xl
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
          <button
            onClick={download}
            disabled={!result}
            className="px-3 py-1 text-[10px] font-semibold rounded bg-navy text-white disabled:opacity-50"
          >Download PNG</button>
        </>
      }
    >
      <div className="grid gap-3" style={{ gridTemplateColumns: '200px 1fr' }}>
        <div className="flex flex-col gap-3">
          <div>
            <div className="form-label">Start from</div>
            <div className="flex flex-col gap-1">
              <Choice name="from" value="all" current={fromBranch ? 'branch' : 'all'} onPick={setFrom}>Whole organisation</Choice>
              {branchRoot ? (
                <Choice name="from" value="branch" current={fromBranch ? 'branch' : 'all'} onPick={setFrom}>
                  {branchRoot.first_name} {branchRoot.last_name} and team
                </Choice>
              ) : (
                <div className="text-[9px] text-gray-400">Select someone on the chart to export just their branch.</div>
              )}
            </div>
          </div>
          <div>
            <div className="form-label">Branches</div>
            <div className="flex flex-col gap-1">
              <Choice name="branches" value="screen" current={branches} onPick={setBranches}>As open on the chart</Choice>
              <Choice name="branches" value="all" current={branches} onPick={setBranches}>Everyone, all branches open</Choice>
            </div>
          </div>
          <div>
            <div className="form-label">Include</div>
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-1.5 text-[10px] text-gray-700 cursor-pointer">
                <input type="checkbox" checked={withTitle} onChange={e => setWithTitle(e.target.checked)} /> Title and date
              </label>
              <label className="flex items-center gap-1.5 text-[10px] text-gray-700 cursor-pointer">
                <input type="checkbox" checked={withLegend} onChange={e => setWithLegend(e.target.checked)} /> Team key
              </label>
            </div>
          </div>
          <div>
            <div className="form-label">Background</div>
            <div className="flex flex-col gap-1">
              <Choice name="bg" value="white" current={background} onPick={setBackground}>White</Choice>
              <Choice name="bg" value="none" current={background} onPick={setBackground}>Transparent, for a coloured slide</Choice>
            </div>
          </div>
        </div>

        <div className="min-w-0">
          <div
            className="border border-gray-200 rounded bg-gray-100 flex items-center justify-center p-2"
            style={{ height: 360 }}
          >
            {result
              ? <img src={result.url} alt="Preview of the downloaded chart" className="max-w-full max-h-full object-contain" />
              : <span className="text-[10px] text-gray-400">{error || 'Drawing…'}</span>}
          </div>
          {result && (
            <div className="mt-1.5 text-[9px] text-gray-500">
              {result.people} {result.people === 1 ? 'person' : 'people'} · {result.width.toLocaleString('en-GB')} × {result.height.toLocaleString('en-GB')} px
            </div>
          )}
          {tooWide && (
            <div className="mt-1.5 px-2.5 py-1.5 rounded border border-amber-300 bg-amber-50 text-amber-800 text-[9px]">
              This is about {Math.round(ratio / (16 / 9))} times as wide as a 16:9 slide is, so the names will be small once it is fitted.
              Shut branches you do not need on the chart, or select a manager and start from them.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function OrgChartPage() {
  const [people,        setPeople]        = useState([]);
  const [teams,         setTeams]         = useState([]);
  const [search,        setSearch]        = useState('');
  const [selected,      setSelected]      = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [viewMode,      setViewMode]      = useState('tree');
  const [photoLightbox, setPhotoLightbox] = useState(null);
  const [transform,     setTransform]     = useState({ x: 0, y: 0, scale: 1 });
  const [isGrabbing,    setIsGrabbing]    = useState(false);
  const [collapsed,     setCollapsed]     = useState(() => new Map());
  const [pendingCentre, setPendingCentre] = useState(null);
  const [showExport,    setShowExport]    = useState(false);

  const dragState    = useRef({ active: false, moved: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  const containerRef = useRef(null);
  const canvasRef    = useRef(null);
  const fitted       = useRef(false);

  useEffect(() => {
    api.get('people').then(p => { setPeople(p || []); setLoading(false); }).catch(() => setLoading(false));
    // Only needed for each team's lead; without it the board still groups by team.
    api.get('teams').then(t => setTeams(t || [])).catch(() => {});
  }, []);

  // Leavers and archived records are not part of the current shape of the
  // company. 'archived' and 'left' are the two statuses this schema actually
  // uses for that; the API already drops 'left' by default, and this keeps the
  // chart right if it is ever asked for everyone. Sub-contractors are engaged
  // through another supplier and are not part of the organisation at all, so
  // they are off the whole page, both views.
  const active = useMemo(() => people.filter(p =>
    p.employee_status !== 'archived' && p.employee_status !== 'left' && !p.sub_contractor
  ), [people]);

  // The tree is the reporting hierarchy, and contractors do not sit on it: they
  // are engaged, not managed. They stay in the list view. buildTree already
  // promotes anyone whose manager is missing from its input to a root, so a
  // report of a contractor still appears.
  const treePeople = useMemo(() => active.filter(p => !isContractor(p)), [active]);

  // Rebuilding and re-sorting the tree is not free, and panning sets state on
  // every mouse move. Without this the whole hierarchy was rebuilt, re-sorted
  // and re-measured on every frame of a drag.
  const roots  = useMemo(() => sortTree(buildTree(treePeople)), [treePeople]);
  const cycles = useMemo(() => reportingCycles(active), [active]);
  // Team membership rather than reporting, so contractors belong here.
  const board  = useMemo(() => groupByTeam(active, teams), [active, teams]);

  // Which branches start shut, worked out during the render rather than in an
  // effect afterwards. Applying it afterwards meant the first layout was the
  // fully expanded tree, and fit-to-view measured THAT: a chart already
  // collapsed to 1714px was fitted as though it were 5349px, so it opened at
  // the minimum zoom.
  const defaultCollapsed = useMemo(() => {
    const deep = new Set();
    const walk = (node, depth) => {
      if (depth >= OPEN_TO_DEPTH && node.children.length > 0) deep.add(node.id);
      node.children.forEach(c => walk(c, depth + 1));
    };
    roots.forEach(r => walk(r, 0));
    return deep;
  }, [roots]);

  // Only the branches somebody has actually clicked are stored, as id -> shut.
  // Anything untouched follows the default above, so reloading with new people
  // in it does not resurrect a stale set of ids.
  const isCollapsed = useCallback(
    id => (collapsed.has(id) ? collapsed.get(id) : defaultCollapsed.has(id)),
    [collapsed, defaultCollapsed],
  );

  const isInCycle = useCallback(id => cycles.has(id), [cycles]);

  const toggle = useCallback((id) => {
    setCollapsed(prev => {
      const next = new Map(prev);
      next.set(id, !(next.has(id) ? next.get(id) : defaultCollapsed.has(id)));
      return next;
    });
  }, [defaultCollapsed]);

  // ── Pan/zoom ──
  const zoomBy = useCallback((factor) => {
    const el = containerRef.current;
    const cx = el ? el.clientWidth / 2 : 0;
    const cy = el ? el.clientHeight / 2 : 0;
    setTransform(t => zoomAt(t, factor, cx, cy));
  }, []);

  const fitToView = useCallback(() => {
    const el = containerRef.current;
    const canvas = canvasRef.current;
    if (!el || !canvas) return;
    // offsetWidth/Height are the pre-transform layout size, so the current zoom
    // does not feed back into the measurement.
    setTransform(fitTransform({
      contentW: canvas.offsetWidth,
      contentH: canvas.offsetHeight,
      viewW: el.clientWidth,
      viewH: el.clientHeight,
      padding: 16,
    }));
  }, []);

  /** Bring one person's card to the middle of the pane at the current zoom. */
  // Moves a card to the middle of the pane by measuring how far off centre it
  // is ON SCREEN and translating by exactly that, rather than recomputing an
  // absolute position from content coordinates.
  //
  // The absolute version was wrong by a fixed ~105px however it was scheduled,
  // because it mixed three bases that do not all update together: the card's
  // offset inside the canvas, the pane's clientWidth, and the transform's own
  // translate. Both rects here are read in the same instant from live DOM, so
  // the delta is correct whatever the bookkeeping says. `translate` is applied
  // in the parent's coordinate space, ahead of `scale`, so one unit is one
  // screen pixel and the delta needs no scaling.
  //
  // The measurement happens outside the updater and only the delta goes in,
  // which keeps the updater a pure function of its argument: React may call it
  // more than once per update, and does under StrictMode in development.
  const centreOn = useCallback((personId) => {
    const el = containerRef.current;
    const canvas = canvasRef.current;
    const card = canvas && canvas.querySelector(`[data-person-id="${personId}"]`);
    if (!el || !canvas || !card) return;
    const cardRect = card.getBoundingClientRect();
    const paneRect = el.getBoundingClientRect();
    const dx = (paneRect.left + paneRect.width  / 2) - (cardRect.left + cardRect.width  / 2);
    const dy = (paneRect.top  + paneRect.height / 2) - (cardRect.top  + cardRect.height / 2);
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
    setTransform(t => ({ ...t, x: t.x + dx, y: t.y + dy }));
  }, []);

  // Fit once the collapsed set above has been applied and laid out.
  //
  // A plain requestAnimationFrame is too early: it can fire before the canvas
  // has a layout size, fitTransform is handed a zero and correctly declines to
  // divide by it, and the chart stays at the origin. Waiting for the canvas to
  // actually have a size is the reliable signal.
  useEffect(() => {
    if (loading || roots.length === 0 || viewMode !== 'tree') return;
    const canvas = canvasRef.current;
    const pane = containerRef.current;
    if (!canvas || !pane) return;
    // All FOUR dimensions have to be real before this is worth latching. The
    // canvas is absolutely positioned, so it gets its size as soon as the cards
    // render, which can be a frame before the flex row has given the pane any
    // height. Checking only the two widths let the fit latch against a pane of
    // zero height: fitTransform correctly declined to divide by zero and handed
    // back the identity transform, and because `fitted` was already set it was
    // never tried again. The chart then sat at the origin at scale 1, which is
    // the exact symptom this effect exists to prevent.
    //
    // The pane is observed as well as the canvas for the same reason: it is the
    // pane gaining its height that unblocks the fit.
    const PAD = 32; // the padding fitToView passes, doubled
    const tryFit = () => {
      if (fitted.current) return;
      if (canvas.offsetWidth > 0 && canvas.offsetHeight > 0
          && pane.clientWidth > PAD && pane.clientHeight > PAD) {
        fitted.current = true;
        fitToView();
      }
    };
    tryFit();
    const ro = new ResizeObserver(tryFit);
    ro.observe(canvas);
    ro.observe(pane);
    return () => ro.disconnect();
  }, [loading, viewMode, roots, fitToView]);

  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    setIsGrabbing(true);
    dragState.current = {
      active: true, moved: false,
      startX: e.clientX, startY: e.clientY,
      origX: transform.x, origY: transform.y,
    };
  }, [transform.x, transform.y]);

  const onMouseMove = useCallback((e) => {
    if (!dragState.current.active) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
      dragState.current.moved = true;
      setTransform(t => ({ ...t, x: dragState.current.origX + dx, y: dragState.current.origY + dy }));
    }
  }, []);

  const onMouseUp = useCallback(() => {
    setIsGrabbing(false);
    dragState.current.active = false;
    // Cleared after the click event that follows this mouseup, so a drag that
    // finishes over a card does not also select it.
    setTimeout(() => { dragState.current.moved = false; }, 80);
  }, []);

  // Wheel has to be attached by hand to be non-passive, and zooming about the
  // pointer needs the pane's own coordinates.
  //
  // `loading` is in the dependency list because the pane does not exist on the
  // first render: the component mounts loading, so containerRef is still null,
  // the effect bailed out, and with only [viewMode] to react to it never ran
  // again. Scroll-to-zoom was therefore dead on arrival, every time.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      setTransform(t => zoomAt(t, factor, e.clientX - rect.left, e.clientY - rect.top));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [viewMode, loading]);

  const handleSelectPerson = useCallback((p) => {
    setSelected(prev => prev?.id === p.id ? null : p);
  }, []);

  /** A search hit is opened up and brought into view, not just selected. */
  const revealPerson = useCallback((p) => {
    setCollapsed(prev => {
      const next = new Map(prev);
      ancestorIds(active, p.id).forEach(id => next.set(id, false));
      next.set(p.id, false);
      return next;
    });
    setSelected(p);
    setSearch('');
    // Centred by the effect below rather than here. Selecting somebody opens the
    // detail pane, which takes 230px off the width of the pane being centred
    // in, and expanding their managers changes the layout again. Measuring on a
    // requestAnimationFrame raced both and landed the card off screen.
    setPendingCentre(p.id);
  }, [active]);

  // Runs after the DOM is updated for the batch that expanded the ancestors and
  // opened the detail pane, so the geometry it reads is the final geometry.
  useEffect(() => {
    if (!pendingCentre || viewMode !== 'tree') return;
    // Exactly one pass. This effect runs after the DOM is committed for the
    // batch that expanded the ancestors AND opened the detail pane, so the pane
    // is already its final 230px-narrower width and one measurement is right.
    //
    // A second pass on a requestAnimationFrame is actively wrong here: the frame
    // can fire before the transform from the first pass has committed, so it
    // measures DOM that has already moved while reading the pre-move transform,
    // and applies the correction twice.
    centreOn(pendingCentre);
    setPendingCentre(null);
  }, [pendingCentre, viewMode, centreOn, collapsed, selected]);

  const searchLower = search.toLowerCase();
  const matchText = p => `${p.first_name} ${p.last_name} ${p.designation || ''} ${p.team_name || ''}`.toLowerCase();
  const matches = search ? active.filter(p => matchText(p).includes(searchLower)) : [];

  const selectedManager = selected ? people.find(p => p.id === selected.reporting_manager_id) : null;

  const teamNames = useMemo(() => [...new Set(active.map(p => p.team_name).filter(Boolean))], [active]);
  // Memoised because the download dialog redraws its preview when this changes.
  const teamColor = useMemo(() => {
    const TEAM_COLORS = ['#1B2A4A','#A1723D','#2563EB','#059669','#7C3AED','#DC2626','#0891B2','#D97706'];
    const out = {};
    teamNames.forEach((t, i) => { out[t] = TEAM_COLORS[i % TEAM_COLORS.length]; });
    return out;
  }, [teamNames]);

  const directCount = useMemo(() => {
    const counts = {};
    active.forEach(p => {
      if (p.reporting_manager_id) counts[p.reporting_manager_id] = (counts[p.reporting_manager_id] || 0) + 1;
    });
    return counts;
  }, [active]);
  const contractorCount = active.filter(isContractor).length;

  const cycleNames = [...cycles]
    .map(id => active.find(p => p.id === id))
    .filter(Boolean)
    .map(p => `${p.first_name} ${p.last_name}`);

  return (
    <div className="fade-in flex flex-col h-full min-w-0">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3 flex-shrink-0 flex-wrap">
        <div>
          <h2 className="text-base font-extrabold text-navy">Organisation Chart</h2>
          <div className="text-[9px] text-gray-500">
            {active.length} active · {contractorCount > 0 && `${contractorCount} contractor${contractorCount !== 1 ? 's' : ''} · `}{roots.length} root node{roots.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            className="form-input text-[9px] w-[180px]"
            placeholder="Search people..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {viewMode === 'tree' && roots.length > 0 && (
            <button
              onClick={() => setShowExport(true)}
              title="Download the chart as an image for slides"
              className="px-3 py-1 text-[9px] font-semibold border border-gray-200 rounded bg-white text-navy hover:bg-gray-50"
            >Download</button>
          )}
          <div className="flex border border-gray-200 rounded overflow-hidden">
            {[['tree','Tree'],['teams','Teams'],['list','List']].map(([m, l]) => (
              <button key={m} onClick={() => setViewMode(m)}
                className={`px-3 py-1 text-[9px] font-semibold ${viewMode === m ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              >{l}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Bad reporting data, said out loud. These people used to be dropped from
          the chart without a word, along with everyone below them. */}
      {cycleNames.length > 0 && (
        <div
          className="mb-2 flex-shrink-0 px-2.5 py-1.5 rounded border border-amber-300 bg-amber-50 text-amber-800 text-[9px]"
          title={cycleNames.join(', ')}
        >
          <span className="font-bold">Reporting loop:</span>{' '}
          {cycleNames.join(', ')} {cycleNames.length === 1 ? 'has' : 'have'} a manager who also reports back to them.
          They are shown as their own root until the reporting line is corrected on People.
        </div>
      )}

      {/* Search results */}
      {search && (
        <div className="mb-2 flex flex-wrap gap-1 flex-shrink-0">
          {matches.slice(0, 8).map(p => (
            <button key={p.id} onClick={() => revealPerson(p)}
              className="flex items-center gap-1.5 px-2 py-1 bg-white border border-gold/40 rounded text-[9px] hover:bg-gold/5">
              {p.profile_photo ? (
                <img src={p.profile_photo} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-5 h-5 rounded-full flex items-center justify-center text-[6px] font-bold text-white flex-shrink-0"
                  style={{ background: p.avatar_color || '#A1723D' }}>
                  {initials(`${p.first_name} ${p.last_name}`)}
                </div>
              )}
              <span className="font-semibold text-navy">{p.first_name} {p.last_name}</span>
              <span className="text-gray-400">{p.designation}</span>
              {isContractor(p) && <span className="text-[7px] bg-amber-100 text-amber-700 px-1 rounded font-bold">CTR</span>}
            </button>
          ))}
          {matches.length === 0 && <span className="text-[9px] text-gray-400">No matches</span>}
          {matches.length > 8 && <span className="text-[9px] text-gray-400">+{matches.length - 8} more</span>}
        </div>
      )}

      {loading && <div className="text-center py-12 text-[10px] text-gray-400">Loading…</div>}

      {/* ── Tree view ── */}
      {!loading && viewMode === 'tree' && (
        <div className="flex gap-3 flex-1 overflow-hidden min-h-0 min-w-0">
          {/* min-w-0 is load-bearing. Without it this flex item takes its
              min-content width from the canvas inside, which is as wide as the
              whole hierarchy, so overflow-hidden never clipped and the entire
              page grew a multi-thousand-pixel horizontal scrollbar. */}
          <div
            ref={containerRef}
            className="flex-1 min-w-0 overflow-hidden bg-gray-50 border border-gray-200 rounded relative select-none"
            style={{ cursor: isGrabbing ? 'grabbing' : 'grab' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            {/* Zoom controls */}
            <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }} className="flex flex-col gap-1">
              {[
                { label: '+', action: () => zoomBy(1.2),   title: 'Zoom in' },
                { label: '−', action: () => zoomBy(1/1.2), title: 'Zoom out' },
                { label: '⛶', action: fitToView,           title: 'Fit the whole chart' },
              ].map(({ label, action, title }) => (
                <button
                  key={label}
                  onClick={(e) => { e.stopPropagation(); action(); }}
                  onMouseDown={e => e.stopPropagation()}
                  title={title}
                  className="w-7 h-7 rounded-md bg-white border border-gray-200 text-gray-700 text-[13px] font-bold flex items-center justify-center hover:bg-gray-50"
                >{label}</button>
              ))}
            </div>

            {/* Hint */}
            <div
              style={{ position: 'absolute', bottom: 8, left: 12, zIndex: 10, pointerEvents: 'none' }}
              className="text-[8px] text-gray-400"
            >
              Drag to pan · scroll to zoom · click face to enlarge photo
            </div>

            {roots.length === 0 && (
              <div className="flex items-center justify-center h-full text-[10px] text-gray-400">No people to display</div>
            )}

            {/* Tree canvas.
                Absolutely positioned, and that is the fix for the page-wide
                horizontal scrollbar rather than a tidiness preference. An
                in-flow canvas as wide as the hierarchy sets the min-content
                width of every ancestor, and `.main`/`.content` in the app shell
                are flex items with min-width:auto, so they grew to match and
                took the sidebar and header with them. min-w-0 alone cannot
                stop that: it lets a flex item shrink, but does not reduce the
                min-content width being propagated upward. Out of flow, the
                canvas contributes nothing to intrinsic sizing, so
                overflow-hidden on the pane finally has something to clip. */}
            <div
              ref={canvasRef}
              style={{
                position: 'absolute', top: 0, left: 0,
                transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                transformOrigin: '0 0',
                display: 'inline-flex', gap: 40, padding: 32,
                width: 'max-content', alignItems: 'flex-start',
                willChange: 'transform',
              }}
            >
              {roots.map(root => (
                <PersonCard
                  key={root.id}
                  node={root}
                  onSelect={handleSelectPerson}
                  selectedId={selected?.id}
                  onPhotoClick={setPhotoLightbox}
                  dragState={dragState}
                  isCollapsed={isCollapsed}
                  onToggle={toggle}
                  isInCycle={isInCycle}
                />
              ))}
            </div>
          </div>

          {selected && (
            <DetailPane
              person={selected}
              manager={selectedManager}
              onClose={() => setSelected(null)}
              onPhotoClick={setPhotoLightbox}
            />
          )}
        </div>
      )}

      {/* ── Team view ── */}
      {!loading && viewMode === 'teams' && (
        <div className="flex gap-3 flex-1 overflow-hidden min-h-0 min-w-0">
          <div className="flex-1 min-w-0 overflow-auto">
            {board.length === 0
              ? <div className="text-center py-12 text-[10px] text-gray-400">No people to display</div>
              : (
                <TeamBoard
                  board={board}
                  colorOf={t => (t.key ? teamColor[t.name] : null) || '#9CA3AF'}
                  selectedId={selected?.id}
                  isHighlighted={p => !!search && matchText(p).includes(searchLower)}
                  onSelect={handleSelectPerson}
                  onPhotoClick={setPhotoLightbox}
                />
              )}
          </div>
          {selected && (
            <DetailPane
              person={selected}
              manager={selectedManager}
              onClose={() => setSelected(null)}
              onPhotoClick={setPhotoLightbox}
            />
          )}
        </div>
      )}

      {/* ── List view ── */}
      {!loading && viewMode === 'list' && (
        <div className="flex gap-3 flex-1 overflow-hidden min-h-0 min-w-0">
          <div className="flex-1 min-w-0 overflow-auto">
            <div className="bg-white border border-gray-200 rounded">
              <table className="w-full">
                <thead>
                  <tr>
                    {['Name','Designation','Team','Reporting To','Directs','Band','Clearance'].map(h => (
                      <th key={h} className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {active.slice().sort((a, b) => `${a.last_name}${a.first_name}`.localeCompare(`${b.last_name}${b.first_name}`)).map(p => {
                    const mgr = people.find(x => x.id === p.reporting_manager_id);
                    const ctr = isContractor(p);
                    const isHighlighted = search && matchText(p).includes(searchLower);
                    return (
                      <tr key={p.id}
                        className={`border-b border-gray-100 cursor-pointer hover:bg-gold/5 ${selected?.id === p.id ? 'bg-gold/5' : ''} ${isHighlighted ? 'bg-amber-50' : ''}`}
                        onClick={() => setSelected(prev => prev?.id === p.id ? null : p)}
                      >
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={e => { e.stopPropagation(); setPhotoLightbox(p); }}
                              className="flex-shrink-0 hover:opacity-80 transition-opacity"
                            >
                              {p.profile_photo ? (
                                <img src={p.profile_photo} alt="" className="w-6 h-6 rounded-full object-cover"
                                  style={{ border: ctr ? '1.5px dashed rgba(245,158,11,0.8)' : '1.5px solid rgba(229,231,235,0.8)' }} />
                              ) : (
                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[7px] font-bold text-white"
                                  style={{ background: p.avatar_color || '#A1723D', border: ctr ? '1.5px dashed rgba(0,0,0,0.3)' : 'none' }}>
                                  {initials(`${p.first_name} ${p.last_name}`)}
                                </div>
                              )}
                            </button>
                            <span className="text-[10px] font-semibold text-navy">{p.first_name} {p.last_name}</span>
                            {ctr && <span className="text-[6px] font-bold bg-amber-100 text-amber-700 px-1 py-0.5 rounded">CTR</span>}
                            {cycles.has(p.id) && (
                              <span className="text-[6px] font-bold bg-amber-100 text-amber-700 px-1 py-0.5 rounded" title="Reporting loop">LOOP</span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-[9px] text-gray-600">{p.designation || '-'}</td>
                        <td className="px-2 py-1.5">
                          {p.team_name ? (
                            <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded"
                              style={{ background: teamColor[p.team_name] + '20', color: teamColor[p.team_name] }}>
                              {p.team_name}
                            </span>
                          ) : <span className="text-[9px] text-gray-400">-</span>}
                        </td>
                        <td className="px-2 py-1.5 text-[9px] text-gray-500">
                          {mgr ? `${mgr.first_name} ${mgr.last_name}` : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {directCount[p.id] ? <span className="text-[9px] font-bold text-navy">{directCount[p.id]}</span> : <span className="text-[9px] text-gray-400">-</span>}
                        </td>
                        <td className="px-2 py-1.5 text-center text-[9px] text-gray-500">{p.rate_band || '-'}</td>
                        <td className="px-2 py-1.5 text-center">
                          {p.security_clearance && p.security_clearance !== 'none' ? (
                            <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-blue-50 text-blue-600">{p.security_clearance.toUpperCase()}</span>
                          ) : <span className="text-[9px] text-gray-400">-</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {selected && (
            <DetailPane
              person={selected}
              manager={selectedManager}
              onClose={() => setSelected(null)}
              onPhotoClick={setPhotoLightbox}
            />
          )}
        </div>
      )}

      {/* Legend */}
      {!loading && (
        <div className="mt-3 flex items-center gap-4 flex-wrap flex-shrink-0">
          {teamNames.map(t => (
            <div key={t} className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full" style={{ background: teamColor[t] }} />
              <span className="text-[8px] text-gray-500">{t} ({active.filter(p => p.team_name === t).length})</span>
            </div>
          ))}
          {/* The dashed-border key only means something where contractors are
              drawn, which is the team and list views. In tree view they are not on the
              chart, so say where they went instead of keying a mark that
              never appears. */}
          {viewMode !== 'tree' && contractorCount > 0 && (
            <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-gray-200">
              <div className="w-4 h-4 rounded-full border-2 border-dashed border-amber-400 flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              </div>
              <span className="text-[8px] text-gray-500">Contractor ({contractorCount})</span>
            </div>
          )}
          {viewMode === 'tree' && contractorCount > 0 && (
            <span className="text-[8px] text-gray-400 ml-2 pl-2 border-l border-gray-200">
              {contractorCount} contractor{contractorCount !== 1 ? 's' : ''} shown in the team and list views only
            </span>
          )}
        </div>
      )}

      {showExport && (
        <ExportDialog
          roots={roots}
          isCollapsed={isCollapsed}
          teamColor={teamColor}
          selected={selected}
          onClose={() => setShowExport(false)}
        />
      )}

      {/* Photo lightbox */}
      {photoLightbox && (
        <PhotoLightbox person={photoLightbox} onClose={() => setPhotoLightbox(null)} />
      )}
    </div>
  );
}
